import {
  Stack,
  Box,
  FormHelperText,
  CircularProgress,
  Card,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Button,
  Chip,
  Typography,
  InputAdornment,
  IconButton,
  alpha,
} from "@mui/material";
import { Fragment, useMemo, useState } from "react";
import { CustomRendererProps } from "../types";
import useData from "./hooks/useData";
import { withJsonFormsControlProps } from "@jsonforms/react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

type Permission = {
  id: number;
  name: string;
  description?: string;
};

const actionLabels: Record<string, string> = {
  archive: "Arquivar",
  create: "Criar",
  dashboard: "Dashboard",
  delete: "Excluir",
  list: "Listar",
  read: "Visualizar",
  send: "Enviar",
  sync: "Sincronizar",
  update: "Editar",
  upsert: "Criar/editar",
};

const getPermissionParts = (permissionName: string) => {
  const [action, ...resourceParts] = permissionName.split(":");
  const resource = resourceParts.join(":") || permissionName;

  return {
    action,
    actionLabel: actionLabels[action] || action,
    resource,
  };
};

const formatPermissionLabel = (permissionName: string) => {
  const { actionLabel, resource } = getPermissionParts(permissionName);
  return `${actionLabel} ${resource}`;
};

function SelectPermissions({
  data: selectedPermissions,
  handleChange,
  path,
  label,
  required,
  errors,
  enabled,
}: CustomRendererProps<number[] | undefined>) {
  const { permissions, loading } = useData();
  const [search, setSearch] = useState("");

  const selectedSet = useMemo(() => new Set(selectedPermissions ?? []), [selectedPermissions]);

  const sortedPermissions = useMemo(() => {
    return [...permissions].sort((a, b) => {
      const aParts = getPermissionParts(a.name);
      const bParts = getPermissionParts(b.name);
      const resourceCompare = aParts.resource.localeCompare(bParts.resource);

      if (resourceCompare !== 0) return resourceCompare;

      return aParts.action.localeCompare(bParts.action);
    });
  }, [permissions]);

  const filteredPermissions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return sortedPermissions;

    return sortedPermissions.filter((permission) => {
      const labelText = formatPermissionLabel(permission.name).toLowerCase();
      const descriptionText = permission.description?.toLowerCase() || "";

      return (
        permission.name.toLowerCase().includes(normalizedSearch) ||
        labelText.includes(normalizedSearch) ||
        descriptionText.includes(normalizedSearch)
      );
    });
  }, [search, sortedPermissions]);

  const enabledFilteredCount = filteredPermissions.filter((permission) =>
    selectedSet.has(permission.id)
  ).length;

  const setData = (selectedItems: number[]) => {
    handleChange(path, selectedItems.length ? selectedItems : undefined);
  };

  const handleTogglePermission = (permissionId: number) => {
    const nextSelected = new Set(selectedPermissions ?? []);

    if (nextSelected.has(permissionId)) {
      nextSelected.delete(permissionId);
    } else {
      nextSelected.add(permissionId);
    }

    setData(Array.from(nextSelected));
  };

  const handleSetFiltered = (checked: boolean) => {
    const nextSelected = new Set(selectedPermissions ?? []);

    filteredPermissions.forEach((permission) => {
      if (checked) {
        nextSelected.add(permission.id);
      } else {
        nextSelected.delete(permission.id);
      }
    });

    setData(Array.from(nextSelected));
  };

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ color: "#7b809a" }} />
      </Stack>
    );
  }

  return (
    <Fragment>
      <Stack gap={1.25} margin="10px 0">
        <Box
          component="p"
          sx={{
            fontFamily: "Roboto",
            fontSize: "0.9rem",
            fontWeight: "400",
            lineHeight: "1.4375em",
            letterSpacing: "0.00938em",
            padding: 0,
            margin: 0,
            position: "relative",
            color: !!errors ? "#F44335" : "#7b809a",
          }}
        >
          {label}
          {required && " *"}
        </Box>

        <Card
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            boxShadow: "none",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ p: 1.25, pb: 0.75 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`${selectedPermissions?.length || 0}/${permissions.length} ativas`}
                variant="outlined"
                sx={{
                  borderColor: "#94a3b8",
                  color: "#334155",
                  bgcolor: "#f8fafc",
                  fontWeight: 600,
                }}
              />
              <Chip
                size="small"
                label={`${enabledFilteredCount}/${filteredPermissions.length} no filtro`}
                variant="outlined"
                sx={{
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  bgcolor: "#fff",
                  fontWeight: 600,
                }}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={!enabled || filteredPermissions.length === 0}
                onClick={() => handleSetFiltered(true)}
                sx={{
                  borderColor: "#16a34a",
                  color: "#166534",
                  bgcolor: "#f0fdf4",
                  "&:hover": {
                    borderColor: "#15803d",
                    bgcolor: "#dcfce7",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e5e7eb",
                    color: "#cbd5e1",
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                Ligar tudo
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!enabled || filteredPermissions.length === 0}
                onClick={() => handleSetFiltered(false)}
                sx={{
                  borderColor: "#f97316",
                  color: "#9a3412",
                  bgcolor: "#fff7ed",
                  "&:hover": {
                    borderColor: "#ea580c",
                    bgcolor: "#ffedd5",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e5e7eb",
                    color: "#cbd5e1",
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                Desligar tudo
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ px: 1.25, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Buscar permissao"
              value={search}
              onChange={(evt) => setSearch(evt.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List
            dense
            disablePadding
            sx={{
              maxHeight: 500,
              overflowY: "auto",
              bgcolor: "background.paper",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            {filteredPermissions.map((permission: Permission, index) => {
              const checked = selectedSet.has(permission.id);
              const { actionLabel, resource } = getPermissionParts(permission.name);
              const previous = filteredPermissions[index - 1];
              const previousResource = previous ? getPermissionParts(previous.name).resource : "";
              const showResourceHeader = resource !== previousResource;

              return (
                <Fragment key={permission.id}>
                  {showResourceHeader && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: "#f8fafc",
                        borderTop: index === 0 ? 0 : "1px solid #e5e7eb",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="#475569"
                        fontWeight={700}
                        sx={{ textTransform: "uppercase", letterSpacing: 0 }}
                      >
                        {resource}
                      </Typography>
                    </Box>
                  )}

                  <ListItem
                    divider
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={checked}
                        disabled={!enabled}
                        onChange={() => handleTogglePermission(permission.id)}
                        inputProps={{ "aria-label": permission.name }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#16a34a",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            bgcolor: "#16a34a",
                          },
                        }}
                      />
                    }
                    sx={{
                      minHeight: 46,
                      py: 0.35,
                      pl: 2,
                      pr: 7.5,
                      bgcolor: checked ? alpha("#16a34a", 0.06) : "transparent",
                      "& .MuiListItemSecondaryAction-root": {
                        right: 12,
                      },
                    }}
                  >
                    <ListItemText
                      primary={actionLabel}
                      secondary={permission.description || permission.name}
                      primaryTypographyProps={{
                        fontWeight: checked ? 700 : 500,
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        color: "#111827",
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.78rem",
                          color: "#64748b",
                          lineHeight: 1.25,
                        },
                      }}
                    />
                  </ListItem>
                </Fragment>
              );
            })}

            {!filteredPermissions.length && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma permissao encontrada.
                </Typography>
              </Box>
            )}
          </List>
        </Card>

        {!!errors && <FormHelperText error>{errors}</FormHelperText>}
      </Stack>
    </Fragment>
  );
}

export default withJsonFormsControlProps(SelectPermissions);
